import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { IconButton } from "rsuite";
import InfoRoundIcon from "@rsuite/icons/InfoRound";
import CloseIcon from "@rsuite/icons/Close";
import { selectSelectedRateplan } from "../../slices/rateplanSlice";
import {
  selectRoomtypes,
  selectSelectedRoomtype,
} from "../../slices/roomtypeSlice";
import {
  buildOccupancyMessage,
  resolveSharedOccupancy,
} from "./occupancyBannerUtils";
import "./occupancyBanner.scss";

/**
 * The dismissible "rates are based on N adults sharing" banner above the ARI
 * calendar and table.
 *
 * Both the rate plan and the room type come from the store rather than props:
 * RoomtypeTabs renders only the selected room type's panel, so a tab switch is
 * a store update, and reading it here is what keeps the number in step with
 * the tab without either view having to thread it down.
 *
 * @param {string} storageKey session-storage key holding the dismissed state.
 *   Per view and per property, so dismissing it on one does not hide the other.
 * @param {boolean} [defaultVisible] shown when nothing has been stored yet.
 * @param {string} [placeholderClassName] the fixed-height wrapper; each view
 *   reserves a different amount of room.
 * @param {string} [contentClassName] extra class on the banner itself.
 */
const OccupancyInfoBanner = ({
  storageKey,
  defaultVisible = true,
  placeholderClassName,
  contentClassName,
}) => {
  const selectedRateplan = useSelector(selectSelectedRateplan);
  const roomtypes = useSelector(selectRoomtypes);
  const selectedRoomtype = useSelector(selectSelectedRoomtype);

  const roomtype = roomtypes?.find(({ id }) => id === selectedRoomtype);
  const occupancy = resolveSharedOccupancy(selectedRateplan, roomtype);

  const [showInfo, setShowInfo] = useState(() => {
    const stored = sessionStorage.getItem(storageKey);
    return stored === null ? defaultVisible : stored === "true";
  });

  useEffect(() => {
    sessionStorage.setItem(storageKey, showInfo);
  }, [showInfo, storageKey]);

  // Re-read when the key changes, i.e. when the property changed underneath us.
  // defaultVisible is deliberately not a dependency: it decides what to show
  // when nothing is stored for a property, so reacting to it would re-show a
  // banner the user had already dismissed for the property they are on.
  useEffect(() => {
    const stored = sessionStorage.getItem(storageKey);
    setShowInfo(stored === null ? defaultVisible : stored === "true");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  if (!showInfo) return null;

  return (
    <div className={placeholderClassName}>
      <div className={classNames("info-banner", contentClassName)}>
        <InfoRoundIcon className="me-3" data-testid="info-icon" />
        <span className="info-banner__message">
          {buildOccupancyMessage(occupancy)}
        </span>
        <IconButton
          icon={<CloseIcon />}
          size="sm"
          appearance="subtle"
          onClick={() => setShowInfo(false)}
          data-testid="close-button"
        />
      </div>
    </div>
  );
};

OccupancyInfoBanner.propTypes = {
  storageKey: PropTypes.string.isRequired,
  defaultVisible: PropTypes.bool,
  placeholderClassName: PropTypes.string,
  contentClassName: PropTypes.string,
};

export default OccupancyInfoBanner;
